import { useState } from 'react';
import {
  alpha,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useCanvasGroupCtx } from './CanvasGroupContext';
import { EditGroupDialog } from './EditGroupDialog';

export interface GroupPickerMenuProps {
  /** Component this picker is bound to. */
  componentId: number;
  /** The DOM element the menu anchors to. `null` when closed. */
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

/**
 * Popover anchored to a component's group link button. Lets the user pick
 * which group the component belongs to (or none), create a new one inline,
 * or open the workspace-wide Manage dialog.
 */
export const GroupPickerMenu = ({
  componentId,
  anchorEl,
  onClose,
}: GroupPickerMenuProps) => {
  const theme = useTheme();
  const {
    groups,
    groupForComponent,
    createGroup,
    setComponentGroup,
    pickDefaultColor,
  } = useCanvasGroupCtx();

  const currentGroup = groupForComponent(componentId);
  const [createOpen, setCreateOpen] = useState(false);

  const closeAndDo = (fn: () => void | Promise<void>) => {
    onClose();
    void fn();
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={anchorEl != null}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 260,
              mt: 0.75,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              boxShadow: `0 6px 24px ${alpha(
                theme.palette.common.black,
                0.25,
              )}`,
              p: 0.5,
              '& .MuiMenuItem-root': {
                borderRadius: 1,
                py: 0.875,
                px: 1.25,
                fontSize: 13,
              },
            },
          },
        }}
      >
        <Box sx={{ px: 3, pt: 1, pb: 0.75 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: theme.palette.text.secondary,
              lineHeight: 1.2,
            }}
          >
            Symbol group
          </Typography>
          {currentGroup == null && groups.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontSize: 11.5,
                color: theme.palette.text.secondary,
                mt: 0.25,
                lineHeight: 1.4,
              }}
            >
              Pick a group to sync this component's symbol with others.
            </Typography>
          )}
        </Box>

        {groups.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              px: 2,
              py: 2,
              mx: 0.5,
              mb: 0.5,
              borderRadius: 1.5,
              border: `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
              backgroundColor: alpha(theme.palette.background.default, 0.4),
            }}
          >
            <Typography
              component="div"
              variant="caption"
              sx={{
                fontSize: 11.5,
                color: theme.palette.text.secondary,
                lineHeight: 1.4,
                maxWidth: 200,
              }}
            >
              Create a group to sync this component's symbol with others.
            </Typography>
          </Box>
        ) : (
          groups.map((group) => {
            const isCurrent = group.id === currentGroup?.id;
            return (
              <MenuItem
                key={group.id}
                onClick={() =>
                  closeAndDo(() => setComponentGroup(componentId, group.id))
                }
                selected={isCurrent}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: alpha(group.color, 0.12),
                    '&:hover': {
                      backgroundColor: alpha(group.color, 0.18),
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 30 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: group.color,
                      boxShadow: `0 0 0 2px ${alpha(group.color, 0.2)}`,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={group.name}
                  secondary={`${group.componentIds.length} ${
                    group.componentIds.length === 1 ? 'member' : 'members'
                  }`}
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { fontWeight: 500, fontSize: 13 },
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    component: 'div',
                    sx: { fontSize: 11.5, mt: 0.125 },
                  }}
                />
                {isCurrent && (
                  <CheckIcon
                    sx={{
                      fontSize: 16,
                      color: group.color,
                      ml: 1,
                    }}
                  />
                )}
              </MenuItem>
            );
          })
        )}

        {currentGroup != null && [
          <Divider
            key="div-1"
            sx={{
              my: 0.75,
              mx: 0.5,
            }}
          />,
          <MenuItem
            key="remove"
            onClick={() =>
              closeAndDo(() => setComponentGroup(componentId, null))
            }
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.error.main,
                backgroundColor: alpha(theme.palette.error.main, 0.06),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 30, color: 'inherit' }}>
              <RemoveCircleOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Remove from group"
              primaryTypographyProps={{
                variant: 'body2',
                sx: { fontSize: 13 },
              }}
            />
          </MenuItem>,
        ]}

        <Divider
          sx={{
            my: 0.75,
            mx: 0.5,
            borderColor: alpha(theme.palette.primary.main, 0.25),
          }}
        />

        <MenuItem
          onClick={() => {
            onClose();
            setCreateOpen(true);
          }}
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 30, color: 'inherit' }}>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="New group…"
            primaryTypographyProps={{
              variant: 'body2',
              sx: { fontSize: 13, fontWeight: 600 },
            }}
          />
        </MenuItem>
      </Menu>

      <EditGroupDialog
        open={createOpen}
        title="Create a new group"
        initialName=""
        initialColor={pickDefaultColor()}
        onSubmit={(name, color) => {
          setCreateOpen(false);
          // Pre-seed the calling component as the new group's first member.
          void createGroup(name, color, componentId);
        }}
        onClose={() => setCreateOpen(false)}
      />
    </>
  );
};
